import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import Polygon from "@arcgis/core/geometry/Polygon";
import * as projectOperator from "@arcgis/core/geometry/operators/projectOperator";
import * as centroidOperator from "@arcgis/core/geometry/operators/centroidOperator";
import * as differenceOperator from "@arcgis/core/geometry/operators/differenceOperator";
import * as unionOperator from "@arcgis/core/geometry/operators/unionOperator";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import Color from "@arcgis/core/Color";
import { arcgisToGeoJSON } from "@terraformer/arcgis";
import Point from "@arcgis/core/geometry/Point";

export function getArcGISGeometryType(geoJSONType: string) {
  switch (geoJSONType) {
    case "Point":
      return "point" as const;
    case "LineString":
    case "MultiLineString":
      return "polyline" as const;
    case "Polygon":
    case "MultiPolygon":
      return "polygon" as const;
    default:
      return "extent" as const;
  }
}

export function getSymbolForGeometryType(type: string) {
  switch (type.toLowerCase()) {
    case "point":
      return {
        type: "simple-marker" as const,
        color: [242, 113, 30],
        size: "12px",
        outline: {
          color: [169, 72, 10],
          width: 1,
        },
      };
    case "linestring":
    case "multilinestring":
      return {
        type: "simple-line" as const,
        color: [0, 112, 255],
        width: 2,
      };
    case "polygon":
    case "multipolygon":
    default:
      return {
        type: "simple-fill" as const,
        color: [226, 116, 19, 0.15],
        outline: {
          color: [242, 113, 30],
          width: 2,
        },
      };
  }
}

export function getHoverSymbolForGeometryType(type: string) {
  switch (type.toLowerCase()) {
    case "point":
      return {
        type: "simple-marker" as const,
        color: [193, 82, 11],
        size: "12px",
        outline: {
          color: [169, 72, 10],
          width: 1,
        },
      };
    case "linestring":
    case "multilinestring":
      return {
        type: "simple-line" as const,
        color: [255, 165, 0],
        width: 3,
      };
    case "polygon":
    case "multipolygon":
    default:
      return {
        type: "simple-fill" as const,
        color: [0, 112, 255, 0.3],
        outline: {
          color: [0, 112, 255],
          width: 2,
        },
      };
  }
}

export function getHighlightSymbolForGeometryType(type: string): __esri.Symbol {
  switch (type) {
    case "point":
      return new SimpleMarkerSymbol({
        style: "circle",
        color: new Color([0, 122, 194, 0.5]), // Light blue with transparency
        size: 12,
        outline: {
          color: new Color([0, 122, 194, 1]), // Blue
          width: 2,
        },
      });
    case "polyline":
      return new SimpleLineSymbol({
        color: new Color([0, 122, 194, 1]), // Blue
        width: 4,
      });
    case "polygon":
    default:
      return new SimpleFillSymbol({
        color: new Color([0, 122, 194, 0.2]), // Light blue with transparency
        outline: {
          color: new Color([0, 122, 194, 1]), // Blue
          width: 2,
        },
      });
  }
}

/**
 * Gets a selected symbol for the given geometry type (different from highlight)
 */
export function getSelectedSymbolForGeometryType(type: string): __esri.Symbol {
  switch (type) {
    case "point":
      return new SimpleMarkerSymbol({
        style: "circle",
        color: new Color([255, 140, 0, 0.7]), // Orange with transparency
        size: 12,
        outline: {
          color: new Color([255, 69, 0, 1]), // Red-orange
          width: 2,
        },
      });
    case "polyline":
      return new SimpleLineSymbol({
        color: new Color([255, 69, 0, 1]), // Red-orange
        width: 4,
      });
    case "polygon":
    default:
      return new SimpleFillSymbol({
        color: new Color([255, 140, 0, 0.3]), // Orange with transparency
        outline: {
          color: new Color([255, 69, 0, 1]), // Red-orange
          width: 2,
        },
      });
  }
}

// ArcGIS to GeoJSON with reprojection to WGS84
export async function convertToGeoJSON(arcgisGeometry: __esri.GeometryUnion) {
  if (!projectOperator.isLoaded()) await projectOperator.load();
  const outSpatialReference = new SpatialReference({ wkid: 4326 });
  const reprojected = projectOperator.execute(arcgisGeometry, outSpatialReference);
  return arcgisToGeoJSON(reprojected.toJSON());
}

export async function getCentroidFromArcGISGeometry(arcgisGeometry: __esri.GeometryUnion) {
  if (arcgisGeometry.spatialReference.isWGS84) return centroidOperator.execute(arcgisGeometry);
  if (!projectOperator.isLoaded()) await projectOperator.load();
  const outSpatialReference = new SpatialReference({ wkid: 4326 });
  const reprojected = projectOperator.execute(arcgisGeometry, outSpatialReference);
  return centroidOperator.execute(reprojected);
}

export async function getBoundingBoxOfArcGISGeometries(arcgisGeometries: __esri.GeometryUnion[]) {
  let unifiedGeometry: __esri.GeometryUnion | nullish = null;
  if (!arcgisGeometries.length) return null;
  if (arcgisGeometries.length === 1) {
    unifiedGeometry = arcgisGeometries[0];
  } else {
    unifiedGeometry = unionOperator.executeMany(arcgisGeometries);
  }
  if (!unifiedGeometry) return null;

  if (!projectOperator.isLoaded()) await projectOperator.load();
  const outSpatialReference = new SpatialReference({ wkid: 4326 });
  const reprojected = projectOperator.execute(unifiedGeometry, outSpatialReference);
  return reprojected.extent;
}

/**
 * Calculates the coordinate difference between the actual center and the logical center
 * Useful for if you have a "center" point you want to use that isn't the map area's center.
 */
export function getEffectiveCenter(mapView: __esri.MapView | nullish, offset: number) {
  if (!mapView) return null;
  return mapView.toMap({
    x: mapView.width / 2,
    y: offset,
  });
}

export function adjustToEffectiveCenter(mapView: __esri.MapView | nullish, offset: number, lat: number, lng: number) {
  if (!mapView) return null;

  const effectiveCenter = getEffectiveCenter(mapView, offset);
  if (!effectiveCenter) return null;
  const actualCenter = mapView.center;

  if (!effectiveCenter.latitude || !effectiveCenter.longitude) return null;
  if (!actualCenter.latitude || !actualCenter.longitude) return null;

  const latDiff = effectiveCenter.latitude - actualCenter.latitude;
  const lngDiff = effectiveCenter.longitude - actualCenter.longitude;

  return { lat: lat - latDiff, lng: lng + lngDiff };
}

export function invertPolygon(polygon: __esri.GeometryUnion) {
  const world = new Polygon({
    rings: [
      [
        [-180, 90],
        [-180, -90],
        [180, -90],
        [180, 90],
        [-180, 90],
      ],
    ],
    spatialReference: { wkid: 4326 },
  });
  const inverted = differenceOperator.execute(world, polygon);
  return inverted;
}

export async function parseCoordStringAndReproject(coordString: string | null | undefined, wkid: number) {
  const coords = coordString?.split(",").map((coord) => parseFloat(coord.trim()));
  if (!coords || coords.length !== 2 || coords.some(isNaN)) return null;

  const [x, y] = coords;
  const point = new Point({ y, x, spatialReference: { wkid } });
  if (wkid === 4326) return point;

  if (!projectOperator.isLoaded()) await projectOperator.load();
  const outSpatialReference = new SpatialReference({ wkid: 4326 });
  const reprojected = projectOperator.execute(point, outSpatialReference);
  return reprojected as __esri.Point;
}

export async function reprojectAndCreateCoordString(lng: number, lat: number, wkid: number) {
  if (!lng || !lat) return null;

  if (wkid === 4326) return `${lng},${lat}`;
  const point = new Point({ x: lng, y: lat, spatialReference: { wkid: 4326 } });
  if (!projectOperator.isLoaded()) await projectOperator.load();
  const outSpatialReference = new SpatialReference({ wkid });
  const reprojected = projectOperator.execute(point, outSpatialReference) as Point;
  return `${reprojected.x},${reprojected.y}`;
}

export async function reprojectExtentToWGS84(extent: __esri.Extent) {
  if (extent.spatialReference.isWGS84) return extent;
  if (!projectOperator.isLoaded()) await projectOperator.load();
  const outSpatialReference = new SpatialReference({ wkid: 4326 });
  const reprojected = projectOperator.execute(extent, outSpatialReference);
  return reprojected.extent as __esri.Extent;
}
